import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Users, Video, Plus, Edit, Trash2, ExternalLink, CheckCircle2, XCircle, HelpCircle, Repeat } from 'lucide-react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

// Use centralized API config
const API_BASE = API_BASE_URL;

interface MeetingEvent {
  _id: string;
  title: string;
  description?: string;
  scheduledDate: string;
  duration: number;
  meetingLink: string;
  roomId: string;
  mode: 'p2p' | 'agora';
  organizer: {
    _id: string;
    name: string;
    email: string;
  };
  teamId: {
    _id: string;
    name: string;
  };
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
  recurrence?: 'none' | 'daily' | 'weekly' | 'biweekly' | 'monthly';
  recurringSeriesId?: string;
  responses?: Array<{
    userId: string;
    status: 'pending' | 'accepted' | 'declined' | 'maybe';
    respondedAt?: string;
  }>;
}

interface MeetingCalendarProps {
  projectId: string;
  teams: Array<{
    _id: string;
    name: string;
    members: Array<{ _id: string; name: string; email: string }>;
  }>;
  currentUser: {
    _id: string;
    name: string;
    email: string;
  };
}

const MeetingCalendar: React.FC<MeetingCalendarProps> = ({ projectId, teams, currentUser }) => {
  const [meetingEvents, setMeetingEvents] = useState<MeetingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<MeetingEvent | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    scheduledDate: '',
    scheduledTime: '',
    duration: 60,
    teamId: '',
    mode: 'p2p' as 'p2p' | 'agora',
    recurrence: 'none' as 'none' | 'daily' | 'weekly' | 'biweekly' | 'monthly',
    recurrenceEndDate: '',
    recurrenceCount: 10
  });

  // Charger les meeting events
  useEffect(() => {
    loadMeetingEvents();
  }, [projectId]);

  const loadMeetingEvents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/meeting-events/project/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setMeetingEvents(response.data.data);
      }
    } catch (err: any) {
      console.error('Erreur chargement meeting events:', err);
      setError(err.response?.data?.error || 'Error loading meetings');
    } finally {
      setLoading(false);
    }
  };

  // Créer un meeting event
  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const scheduledDateTime = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`);
      
      const payload: Record<string, unknown> = {
        title: formData.title,
        description: formData.description,
        scheduledDate: scheduledDateTime.toISOString(),
        duration: formData.duration,
        projectId,
        teamId: formData.teamId,
        mode: formData.mode
      };
      if (formData.recurrence && formData.recurrence !== 'none') {
        payload.recurrence = formData.recurrence;
        if (formData.recurrenceEndDate) payload.recurrenceEndDate = new Date(formData.recurrenceEndDate).toISOString();
        if (formData.recurrenceCount) payload.recurrenceCount = formData.recurrenceCount;
      }
      const response = await axios.post(
        `${API_BASE}/meeting-events`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setShowCreateModal(false);
        setFormData({
          title: '',
          description: '',
          scheduledDate: '',
          scheduledTime: '',
          duration: 60,
          teamId: '',
          mode: 'p2p',
          recurrence: 'none',
          recurrenceEndDate: '',
          recurrenceCount: 10
        });
        loadMeetingEvents();
      }
    } catch (err: any) {
      console.error('Erreur création meeting event:', err);
      const msg = err.response?.data?.error || err.message || 'Error creating the meeting';
      setError(msg);
    }
  };

  // Supprimer un meeting event
  const handleDeleteEvent = async (eventId: string) => {
    if (!window.confirm('Are you sure you want to delete this meeting?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/meeting-events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadMeetingEvents();
    } catch (err: any) {
      console.error('Erreur suppression meeting event:', err);
      setError(err.response?.data?.error || 'Error deleting the meeting');
    }
  };

  // Répondre à une invitation
  const handleRespondToInvitation = async (eventId: string, status: 'accepted' | 'declined' | 'maybe') => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE}/meeting-events/${eventId}/respond`,
        { status },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      loadMeetingEvents();
    } catch (err: any) {
      console.error('Erreur réponse invitation:', err);
      setError(err.response?.data?.error || 'Error responding to the invitation');
    }
  };

  // Formater la date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Formater l'heure
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Obtenir les événements d'un jour
  const getEventsForDate = (date: Date) => {
    return meetingEvents.filter(event => {
      const eventDate = new Date(event.scheduledDate);
      return (
        eventDate.getDate() === date.getDate() &&
        eventDate.getMonth() === date.getMonth() &&
        eventDate.getFullYear() === date.getFullYear()
      );
    });
  };

  // Obtenir le statut de réponse de l'utilisateur
  const getUserResponse = (event: MeetingEvent) => {
    if (!event.responses) return null;
    return event.responses.find(r => r.userId === currentUser._id);
  };

  // Générer les jours du mois
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];
    
    // Jours vides au début
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Jours du mois
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const days = getDaysInMonth();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="h-6 w-6 text-blue-600" />
            Meeting calendar
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Plan and manage your team meetings
          </p>
        </div>
        <button
          onClick={() => {
            setShowCreateModal(true);
            setSelectedDate(null);
            const today = new Date().toISOString().split('T')[0];
            setFormData({
              title: '',
              description: '',
              scheduledDate: today,
              scheduledTime: '09:00',
              duration: 60,
              teamId: teams.length > 0 ? teams[0]._id : '',
              mode: 'p2p',
              recurrence: 'none',
              recurrenceEndDate: '',
              recurrenceCount: 10
            });
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          New meeting
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-500">Chargement...</div>
      ) : (
        <>
          {/* Navigation du mois */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => {
                const newMonth = new Date(currentMonth);
                newMonth.setMonth(newMonth.getMonth() - 1);
                setCurrentMonth(newMonth);
              }}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              ←
            </button>
            <h3 className="text-lg font-semibold text-gray-900">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h3>
            <button
              onClick={() => {
                const newMonth = new Date(currentMonth);
                newMonth.setMonth(newMonth.getMonth() + 1);
                setCurrentMonth(newMonth);
              }}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              →
            </button>
          </div>

          {/* Calendrier */}
          <div className="grid grid-cols-7 gap-2 mb-6">
            {/* En-têtes des jours */}
            {dayNames.map(day => (
              <div key={day} className="text-center text-xs font-semibold text-gray-600 py-2">
                {day}
              </div>
            ))}
            
            {/* Jours */}
            {days.map((day, index) => {
              if (!day) {
                return <div key={`empty-${index}`} className="h-24"></div>;
              }

              const dayEvents = getEventsForDate(day);
              const isToday = day.toDateString() === new Date().toDateString();
              const isPast = day < new Date() && !isToday;

              return (
                <div
                  key={day.toDateString()}
                  className={`min-h-24 border border-gray-200 rounded-lg p-2 ${
                    isToday ? 'bg-blue-50 border-blue-300' : ''
                  } ${isPast ? 'opacity-60' : ''}`}
                >
                  <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : 'text-gray-700'}`}>
                    {day.getDate()}
                  </div>
                  <div className="space-y-1">
                    {dayEvents.slice(0, 2).map(event => (
                      <div
                        key={event._id}
                        onClick={() => setSelectedEvent(event)}
                        className="text-xs p-1 bg-blue-100 text-blue-800 rounded cursor-pointer hover:bg-blue-200 truncate flex items-center gap-1"
                        title={event.title}
                      >
                        {event.recurrence && event.recurrence !== 'none' && (
                          <Repeat className="h-3 w-3 flex-shrink-0" />
                        )}
                        <span className="truncate">{formatTime(event.scheduledDate)} - {event.title}</span>
                      </div>
                    ))}
                    {dayEvents.length > 2 && (
                      <div className="text-xs text-gray-500">
                        +{dayEvents.length - 2} autre(s)
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Liste des événements à venir */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming meetings</h3>
            <div className="space-y-3">
              {meetingEvents
                .filter(event => new Date(event.scheduledDate) >= new Date())
                .sort((a, b) => new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime())
                .slice(0, 5)
                .map(event => {
                  const userResponse = getUserResponse(event);
                  const isOrganizer = event.organizer._id === currentUser._id;

                  return (
                    <div
                      key={event._id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <h4 className="font-semibold text-gray-900">{event.title}</h4>
                            {event.recurrence && event.recurrence !== 'none' && (
                              <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded flex items-center gap-1">
                                <Repeat className="h-3 w-3" />
                                {event.recurrence === 'weekly' && 'Weekly'}
                                {event.recurrence === 'biweekly' && 'Biweekly'}
                                {event.recurrence === 'monthly' && 'Monthly'}
                                {event.recurrence === 'daily' && 'Daily'}
                              </span>
                            )}
                            {isOrganizer && (
                              <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">
                                Organisateur
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {formatDate(event.scheduledDate)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {formatTime(event.scheduledDate)} ({event.duration} min)
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="h-4 w-4" />
                              {event.teamId.name}
                            </span>
                          </div>
                          {event.description && (
                            <p className="text-sm text-gray-500 mb-2">{event.description}</p>
                          )}
                          <div className="flex items-center gap-2">
                            <a
                              href={event.meetingLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                            >
                              <ExternalLink className="h-4 w-4" />
                              Join meeting
                            </a>
                            {!isOrganizer && !userResponse && (
                              <div className="flex items-center gap-2 ml-4">
                                <button
                                  onClick={() => handleRespondToInvitation(event._id, 'accepted')}
                                  className="p-1 text-green-600 hover:bg-green-50 rounded"
                                  title="Accepter"
                                >
                                  <CheckCircle2 className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleRespondToInvitation(event._id, 'declined')}
                                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                                  title="Refuser"
                                >
                                  <XCircle className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleRespondToInvitation(event._id, 'maybe')}
                                  className="p-1 text-yellow-600 hover:bg-yellow-50 rounded"
                                  title="Maybe"
                                >
                                  <HelpCircle className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                            {userResponse && (
                              <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded ml-2">
                                {userResponse.status === 'accepted' && '✓ Accepted'}
                                {userResponse.status === 'declined' && '✗ Declined'}
                                {userResponse.status === 'maybe' && '? Maybe'}
                              </span>
                            )}
                          </div>
                        </div>
                        {isOrganizer && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleDeleteEvent(event._id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                              title="Supprimer"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              {meetingEvents.filter(event => new Date(event.scheduledDate) >= new Date()).length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No meetings scheduled
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Modal de création */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">Create a new meeting</h3>
            <form onSubmit={handleCreateEvent}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Meeting title *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="E.g.: Weekly meeting"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    placeholder="Meeting description..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date *
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.scheduledDate}
                      onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Heure *
                    </label>
                    <input
                      type="time"
                      required
                      value={formData.scheduledTime}
                      onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duration (minutes) *
                    </label>
                    <input
                      type="number"
                      required
                      min={5}
                      max={480}
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Meeting mode *
                    </label>
                    <select
                      value={formData.mode}
                      onChange={(e) => setFormData({ ...formData, mode: e.target.value as 'p2p' | 'agora' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="p2p">P2P (WebRTC)</option>
                      <option value="agora">Agora</option>
                    </select>
                  </div>
                </div>

                {/* Récurrence - style Google Calendar / Teams */}
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                  <div className="flex items-center gap-2 mb-3">
                    <Repeat className="h-5 w-5 text-blue-600" />
                    <label className="block text-sm font-medium text-gray-700">
                      Recurrence
                    </label>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Frequency</label>
                      <select
                        value={formData.recurrence}
                        onChange={(e) => setFormData({ ...formData, recurrence: e.target.value as 'none' | 'daily' | 'weekly' | 'biweekly' | 'monthly' })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                      >
                        <option value="none">Once</option>
                        <option value="daily">Every day</option>
                        <option value="weekly">Every week</option>
                        <option value="biweekly">Every two weeks</option>
                        <option value="monthly">Every month</option>
                      </select>
                    </div>
                    {formData.recurrence !== 'none' && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Jusqu&apos;au</label>
                          <input
                            type="date"
                            value={formData.recurrenceEndDate}
                            onChange={(e) => setFormData({ ...formData, recurrenceEndDate: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            min={formData.scheduledDate || new Date().toISOString().split('T')[0]}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Ou nombre d&apos;occurrences</label>
                          <input
                            type="number"
                            min={2}
                            max={52}
                            value={formData.recurrenceCount}
                            onChange={(e) => setFormData({ ...formData, recurrenceCount: parseInt(e.target.value) || 10 })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Ex: 10"
                          />
                        </div>
                      </div>
                    )}
                    {formData.recurrence !== 'none' && (
                      <p className="text-xs text-gray-500">
                        Meetings will be created with a unique video-conference link for each occurrence.
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Team *
                  </label>
                  <select
                    required
                    value={formData.teamId}
                    onChange={(e) => setFormData({ ...formData, teamId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a team</option>
                    {teams.map(team => (
                      <option key={team._id} value={team._id}>
                        {team.name} ({team.members.length} membre{team.members.length > 1 ? 's' : ''})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create meeting
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de détails */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">{selectedEvent.title}</h3>
              <button
                onClick={() => setSelectedEvent(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              {selectedEvent.recurrence && selectedEvent.recurrence !== 'none' && (
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                  <Repeat className="h-5 w-5 text-amber-600" />
                  <span className="text-sm font-medium text-amber-800">
                    Recurring meeting: {selectedEvent.recurrence === 'weekly' && 'Chaque semaine'}
                    {selectedEvent.recurrence === 'biweekly' && 'Toutes les deux semaines'}
                    {selectedEvent.recurrence === 'monthly' && 'Chaque mois'}
                    {selectedEvent.recurrence === 'daily' && 'Tous les jours'}
                  </span>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500 mb-1">Date et heure</p>
                <p className="font-medium">
                  {formatDate(selectedEvent.scheduledDate)} at {formatTime(selectedEvent.scheduledDate)}
                </p>
                <p className="text-sm text-gray-500">Duration: {selectedEvent.duration} minutes</p>
              </div>
              {selectedEvent.description && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Description</p>
                  <p>{selectedEvent.description}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500 mb-1">Équipe</p>
                <p className="font-medium">{selectedEvent.teamId.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Organisateur</p>
                <p className="font-medium">{selectedEvent.organizer.name}</p>
              </div>
              <div className="flex items-center gap-2 pt-4 border-t">
                <a
                  href={selectedEvent.meetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Video className="h-5 w-5" />
                  Join meeting
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingCalendar;

